import React, { useState, useEffect, useMemo } from "react";
import styles from "../CustomReportsForm.module.sass";
import { TextArea, TextInput } from "colbi_web_ui/lib/components/inputs";
import { SovosIcon } from "mosaic-react";
import { HighlightOff } from "mosaic-react-icons";
import { metadataStringify, parseJson } from "../../../../../utils";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import { DescriptionsLocales } from "../../../../../components";
import descriptionsLocalesDefaultStyles from "../../../../../components/DescriptionLocales/DescriptionsLocales.module.sass";

const CommandsTab = ({ Content, Metadata, querySelectors, handleChange }) => {
    const [eventType, setEventType] = useState("");
    const [refs, setRefs] = useState({});
    const [scroll, setScroll] = useState(false);
    const [columnsConfigurationFormatted, setColumnsConfigurationFormatted] = useState("{}");
    const [parsedConditionFields, setParsedConditionFields] = useState([]);
    const { i18n } = useGlobalValue();

    const { items: sqlSelectors } = querySelectors || {};
    const parsedColumnsConfiguration = metadataJsonToArray(
        parseJson(columnsConfigurationFormatted),
    );
    const parsedContent = parseJson(Content);
    const columnConfigurationItems = buildColumnConfigurationItems(parsedColumnsConfiguration);
    const contentItems = buildContentItems(parsedContent, sqlSelectors);

    useEffect(() => {
        if (parsedColumnsConfiguration && parsedColumnsConfiguration.length) {
            setRefs(
                (parsedColumnsConfiguration || []).reduce((acc, _, index) => {
                    acc[index] = React.createRef();
                    return acc;
                }, {}),
            );
        }
        if (eventType === "scrollToContainer") {
            setScroll(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnsConfigurationFormatted]);

    useEffect(() => {
        if (scroll && eventType === "scrollToContainer") {
            scrollToItem(parsedColumnsConfiguration.length - 1);
        }
        setScroll(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scroll]);

    useMemo(() => {
        try {
            if (Metadata) {
                const _metadata = parseJson(Metadata);
                const _columnsConfiguration = (_metadata && _metadata.columnsConfiguration) || [];
                if (_columnsConfiguration && _columnsConfiguration.length) {
                    const metadataConverted = _columnsConfiguration.reduce((acc, column) => {
                        const { key, ...rest } = column;
                        const subKey = Object.keys(rest)[0];
                        acc[subKey] = rest[subKey];
                        return acc;
                    });
                    setColumnsConfigurationFormatted(JSON.stringify(metadataConverted));
                } else {
                    setColumnsConfigurationFormatted("{}");
                }

                const _conditionFields = (_metadata && _metadata.availableConditionsFields) || [];
                if (_conditionFields && _conditionFields.length) {
                    setParsedConditionFields(_conditionFields);
                } else {
                    setParsedConditionFields([]);
                }
            } else {
                setColumnsConfigurationFormatted("{}");
                setParsedConditionFields([]);
            }
        } catch (e) {
            console.log(e);
        }
    }, [Metadata]);

    function metadataJsonToArray(jsonInput) {
        return Object.entries(jsonInput).map(([key, value], index) => {
            return {
                key: `${key.split("_")[0]}_${index}`,
                ...value,
            };
        });
    }

    const metadataRevertColumnsConfiguration = (imputArray) => {
        const columnsConfigurationReverted = (imputArray || []).map((item) => {
            const { key, type, align, translations } = item;

            const finalObj = {};
            finalObj[key.split("_")[0] || ""] = {
                type: type,
                align: align,
                translations: translations,
            };

            return finalObj;
        });

        return columnsConfigurationReverted;
    };

    function handleTranslationChange(e, keyToChange, updatedItems) {
        parsedColumnsConfiguration[keyToChange].translations = updatedItems;

        const metadataObj = parsedColumnsConfiguration.length
            ? metadataStringify(metadataRevertColumnsConfiguration(parsedColumnsConfiguration), [
                  ...parsedConditionFields,
              ])
            : null;

        handleChange && handleChange(e, "Metadata", metadataObj);
    }

    const scrollToItem = (id) => {
        return refs[id]?.current.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    function hasContent(content, commands) {
        return content && commands;
    }
    function buildColumnConfigurationItems(Metadata) {
        return (
            <div>
                <div
                    className={`${styles["metadata-entry"]} ${styles["scrollable-metadata-entry"]}`}
                >
                    {(Metadata || []).map((columnMetadata, index) => {
                        const { key: column, type, translations } = columnMetadata || {};

                        return (
                            <div
                                ref={refs[index]}
                                key={`${index}`}
                                className={`${styles["metadata-container"]} ${styles["darker-border"]}`}
                            >
                                <div
                                    className={`${styles["remove-button-container"]} ${styles["flex-end"]}`}
                                >
                                    <button
                                        type="button"
                                        className={styles["remove-button"]}
                                        onClick={(e) => {
                                            Metadata.splice(index, 1);
                                            setEventType("stay");

                                            const metadataObj = Metadata.length
                                                ? metadataStringify(
                                                      metadataRevertColumnsConfiguration(Metadata),
                                                      [...parsedConditionFields],
                                                  )
                                                : null;

                                            handleChange(e, "Metadata", metadataObj);
                                        }}
                                    >
                                        <SovosIcon>
                                            <HighlightOff />
                                        </SovosIcon>
                                    </button>
                                </div>
                                <div className={styles["flex"]}>
                                    <div className={styles["input-field"]}>
                                        <TextInput
                                            className={styles["input"]}
                                            name={i18n`Column`}
                                            placeholder={i18n`Enter a column value`}
                                            value={column.split("_")[0] || ""}
                                            onKeyDown={(e) =>
                                                e.keyCode === 32 && e.preventDefault()
                                            }
                                            onChange={(e) => {
                                                delete Metadata[index];
                                                Metadata[index] = {
                                                    ...columnMetadata,
                                                    key: e.target.value,
                                                };
                                                setEventType("stay");
                                                handleChange(
                                                    e,
                                                    "Metadata",
                                                    metadataStringify(
                                                        metadataRevertColumnsConfiguration(
                                                            Metadata,
                                                        ),
                                                        [...parsedConditionFields],
                                                    ),
                                                );
                                            }}
                                        />
                                    </div>
                                    <div className={styles["input-field"]}>
                                        <TextInput
                                            className={styles["input"]}
                                            name={i18n`Type`}
                                            placeholder={i18n`Enter a type value`}
                                            value={type || ""}
                                            onChange={(e) => {
                                                Metadata[index]["type"] = e.target.value;
                                                setEventType("stay");
                                                handleChange(
                                                    e,
                                                    "Metadata",
                                                    metadataStringify(
                                                        metadataRevertColumnsConfiguration(
                                                            Metadata,
                                                        ),
                                                        [...parsedConditionFields],
                                                    ),
                                                );
                                            }}
                                        />
                                    </div>
                                </div>

                                <DescriptionsLocales
                                    handleChange={handleTranslationChange}
                                    jsonObject={translations}
                                    keyToChange={index}
                                    inputTitle={"Description"}
                                    inputPlaceholder={"Enter a Description"}
                                    styles={{
                                        ...descriptionsLocalesDefaultStyles,
                                        "input-field": `${descriptionsLocalesDefaultStyles["input-field"]} ${styles["margin-1rem"]}`,
                                    }}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }
    function buildColumnConfigurationContainer(columnConfigurationItems, handleMetadataObj) {
        let tempObj = {};
        return (
            <div className={styles["flex"]}>
                <div className={styles["input-field"]}>
                    <h5
                        className={`${styles["label"]} ${styles["no-bottom-margin"]}`}
                    >{i18n`Columns configuration`}</h5>
                    <div>
                        {(handleMetadataObj || []).length ? (
                            columnConfigurationItems
                        ) : (
                            <small>{i18n`No column assigned`}</small>
                        )}
                    </div>
                    <div>
                        <button
                            className={styles["add-button"]}
                            type="button"
                            onClick={(e) => {
                                tempObj = {
                                    align: "",
                                    key: "",
                                    translations: {},
                                    type: "",
                                };

                                handleMetadataObj.push(tempObj);
                                handleChange(
                                    e,
                                    "Metadata",
                                    metadataStringify(
                                        metadataRevertColumnsConfiguration(handleMetadataObj),
                                        [...parsedConditionFields],
                                    ),
                                );
                                setEventType("scrollToContainer");
                            }}
                        >
                            <small>{i18n`[+] Add new column`}</small>
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    function buildContentContainer(contentItems, content) {
        const { query } = content || {};
        let tempObj = {};

        return (
            <div className={styles["flex"]}>
                <div className={styles["input-field"]}>
                    <h5
                        className={`${styles["label"]} ${styles["no-bottom-margin"]}`}
                    >{i18n`Content`}</h5>
                    <div>
                        {hasContent(content, query) ? (
                            contentItems
                        ) : (
                            <small>{i18n`No content assigned`}</small>
                        )}
                    </div>
                    {!hasContent(content, query) ? (
                        <div>
                            <button
                                className={styles["add-button"]}
                                type="button"
                                onClick={(e) => {
                                    tempObj = {
                                        ...content,
                                        ...{
                                            query: {},
                                        },
                                    };
                                    handleChange(e, "Content", JSON.stringify(tempObj));
                                }}
                            >
                                <small>{i18n`[+] Add new content`}</small>
                            </button>
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }
    function buildContentItems(parsedContent, sqlSelectors) {
        const { query } = parsedContent || {};
        return (
            <div>
                <div className={styles["command-content-entry"]}>
                    <div
                        className={`${styles["content-container"]} ${styles["darker-border"]} ${styles["scrollable-container"]}`}
                    >
                        <div
                            className={`${styles["remove-button-container"]} ${styles["flex-end"]} ${styles["selectors-remove-button"]}`}
                        >
                            <button
                                type="button"
                                className={styles["remove-button"]}
                                onClick={(e) => {
                                    handleChange(e, "Content", "");
                                }}
                            >
                                <SovosIcon>
                                    <HighlightOff />
                                </SovosIcon>
                            </button>
                        </div>
                        {(sqlSelectors || []).map(({ id, name }, queryIndex) => {
                            return (
                                <div
                                    key={queryIndex}
                                    className={`${styles["query-content-container"]}`}
                                >
                                    <div className={styles["query-content"]}>
                                        <div
                                            className={`${styles["input-field"]} ${styles["query-key"]}`}
                                        >
                                            <TextInput
                                                style={{
                                                    width: "7.5rem",
                                                    marginTop: "0.4rem",
                                                }}
                                                value={name}
                                                disabled
                                            />
                                        </div>
                                        <div
                                            className={`${styles["input-field"]} ${styles["query-input"]}`}
                                        >
                                            <TextArea
                                                className={styles["input"]}
                                                placeholder={i18n`Enter a value`}
                                                value={(query && query[`${id}`]) || ""}
                                                onChange={(e) => {
                                                    const tempObj = parsedContent;
                                                    query[`${id}`] = e.target.value;
                                                    handleChange(
                                                        e,
                                                        "Content",
                                                        JSON.stringify({ ...tempObj }),
                                                    );
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles["commands"]}>
            <div className={styles["group-content"]}>
                <div className={styles["group-content"]}>
                    {buildContentContainer(contentItems, parsedContent)}
                </div>
                <div className={styles["group-content"]}>
                    {buildColumnConfigurationContainer(
                        columnConfigurationItems,
                        parsedColumnsConfiguration,
                    )}
                </div>
            </div>
        </div>
    );
};

export default CommandsTab;
